import {
  DeleteOutlined,
  PlusOutlined,
  SendOutlined,
  ThunderboltOutlined,
  VerticalAlignTopOutlined,
} from "@ant-design/icons"
import {
  App as AntdApp,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Popover,
  Tag,
  Tooltip,
  Typography,
} from "antd"
import {
  type ChangeEvent,
  type DragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import Flex from "#blocks/Flex"
import { useUserContext } from "#contexts/UserContextProvider"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type Uuid } from "#customTypes/globalTypes"
import { StudentArtworkForDisplay } from "#forms/AddStudent/StudentArtworkSection"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useUpdateDoc } from "#hooks/jotai/useSaveDoc"

import EssayPanel from "./EssayPanel"
import EssayRow from "./EssayRow"
import { describeArrangementError } from "./portfolioArrangement"
import PortfolioComment from "./PortfolioComment"
import { ESSAY_SELECTION_IDS } from "./portfolioEssayConstants"
import {
  categoryColorMap,
  categoryLabelMap,
  categoryTextColorMap,
  PORTFOLIO_CATEGORY_LIMITS,
  statusColorMap,
} from "./portfolioPhotoGridConstants"
import { getDueDateStyle } from "./portfolioPhotoGridHelpers"
import PortfolioProjectModal from "./PortfolioProjectModal"
import PortfolioProjectTabs from "./PortfolioProjectTabs"
import PortfolioStepModal from "./PortfolioStepModal"
import ProjectDetailsPopoverContent from "./ProjectDetailsPopoverContent"
import ProjectRow from "./ProjectRow"
import SegmentDetailRow from "./SegmentDetailRow"
import TimelineSectionHeader from "./TimelineSectionHeader"
import {
  type CreatePortfolioProjectInput,
  PORTFOLIO_MAX_STEPS_PER_PROJECT,
  type PortfolioMockProject,
  type PortfolioMockStage,
  type PortfolioStatementKey,
  usePortfolioDbStorage,
} from "./usePortfolioDbStorage"

const { Text } = Typography

// Inquiry question first, per the AP written-evidence order.
const ESSAY_KEYS_IN_ORDER: PortfolioStatementKey[] = [
  "sustainedInquiryQuestion",
  "sustainedPracticeEvidence",
]

const PortfolioPhotoGrid = ({
  studentId,
  artworks,
  initialProjectId,
}: {
  studentId: Uuid | null
  artworks: StudentArtworkForDisplay[]
  initialProjectId?: string
}) => {
  void artworks
  const { message } = AntdApp.useApp()
  const { effectiveUserId } = useUserContext()
  const currentUserId = effectiveUserId ?? ""
  const student = useGetOneStudent(studentId ?? undefined)
  const { updateDoc: updateStudent } = useUpdateDoc(DOC_TYPES.STUDENTS)

  const {
    addProjectReference,
    addComment,
    addProjectToPriorityLane,
    createProject,
    createStep,
    moveStageToProject,
    planningProjectSummaries,
    priorityLaneProjectIds,
    portfolioProfile,
    projects: projectState,
    reorderProjectPriority,
    reorderProjects,
    reorderStagesInProject,
    deleteStage,
    deleteProject,
    removeProjectReference,
    removeProjectFromPriorityLane,
    selectedEssayKey,
    selectedProject,
    selectedWorkProjects,
    setActiveProjectId,
    sustainedProjects,
    pausedProjects,
    unassignedProjects,
    updatePortfolioProfile,
    timelineEntries,
    updateProject,
    updateProjectReference,
    updateStage,
    updateStatementValue,
    pickRandomProjectIdFromLane,
  } = usePortfolioDbStorage({ studentId })

  const initialProjectAppliedIdRef = useRef<string>()
  useEffect(() => {
    if (
      !initialProjectId ||
      initialProjectAppliedIdRef.current === initialProjectId ||
      !projectState.some((project) => project.id === initialProjectId)
    ) {
      return
    }
    setActiveProjectId(initialProjectId)
    initialProjectAppliedIdRef.current = initialProjectId
  }, [initialProjectId, projectState, setActiveProjectId])

  // Select a project that just appeared. Diffed by id rather than "index 0":
  // the list is arranged by category, so a new project is not necessarily first.
  const knownProjectIdsRef = useRef<Set<string> | null>(null)
  useEffect(() => {
    const currentIds = new Set(projectState.map((project) => project.id))
    const known = knownProjectIdsRef.current
    if (known) {
      const added = projectState.find((project) => !known.has(project.id))
      if (added) setActiveProjectId(added.id)
    }
    knownProjectIdsRef.current = currentIds
  }, [projectState, setActiveProjectId])

  const projectStateRef = useRef(projectState)
  projectStateRef.current = projectState
  useEffect(
    () => () => {
      for (const project of projectStateRef.current) {
        for (const ref of project.references ?? []) {
          if (ref.imageUrl?.startsWith("blob:")) {
            URL.revokeObjectURL(ref.imageUrl)
          }
        }
      }
    },
    []
  )

  const [isStepModalOpen, setIsStepModalOpen] = useState(false)
  const [stepModalProjectId, setStepModalProjectId] = useState<
    string | undefined
  >()
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false)
  const [projectModalInitialCategory, setProjectModalInitialCategory] =
    useState<PortfolioMockProject["category"] | undefined>(undefined)
  const [focusedStageId, setFocusedStageId] = useState<string | null>(null)
  const [activeDetailTab, setActiveDetailTab] = useState<
    "items" | "references"
  >("items")
  const [commentDraft, setCommentDraft] = useState("")
  const [dragProjectId, setDragProjectId] = useState<string | null>(null)
  const [projectInsertIndicator, setProjectInsertIndicator] = useState<{
    zone: "timeline" | "priority"
    projectId: string
    position: "before" | "after"
  } | null>(null)
  const [dragStage, setDragStage] = useState<{
    projectId: string
    stageId: string
  } | null>(null)
  const [projectDropTargetId, setProjectDropTargetId] = useState<string | null>(
    null
  )
  const [projectDropZone, setProjectDropZone] = useState<
    "timeline" | "priority" | null
  >(null)
  const [stageDropTargetId, setStageDropTargetId] = useState<string | null>(
    null
  )
  const referenceInputRef = useRef<HTMLInputElement | null>(null)
  const dragPreviewRef = useRef<HTMLDivElement | null>(null)
  const commentScrollRef = useRef<HTMLDivElement | null>(null)

  const statusOptions: Array<{
    label: string
    value: PortfolioMockStage["status"]
  }> = [
    { label: "Not Started", value: "not_started" },
    { label: "In Progress", value: "in_progress" },
    { label: "Review", value: "review" },
    { label: "Complete", value: "complete" },
    { label: "Has Feedback", value: "has_feedback" },
  ]

  // Either a real project id or an essay selection id — drives which timeline
  // entries show in the history column and where new comments get tagged.
  const activeSelectionId = selectedEssayKey
    ? ESSAY_SELECTION_IDS[selectedEssayKey]
    : selectedProject?.id

  const selectedProjectTimeline = useMemo(
    () =>
      timelineEntries
        .filter((entry) => entry.projectId === activeSelectionId)
        .slice()
        .reverse(),
    [activeSelectionId, timelineEntries]
  )

  useEffect(() => {
    const el = commentScrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [selectedProjectTimeline.length])

  const selectedCountLabel = `${selectedWorkProjects.length}/${PORTFOLIO_CATEGORY_LIMITS.selected}`
  const sustainedCountLabel = `${sustainedProjects.length}/${PORTFOLIO_CATEGORY_LIMITS.sustained}`
  const projectsById = useMemo(
    () =>
      Object.fromEntries(projectState.map((project) => [project.id, project])),
    [projectState]
  )
  const priorityLaneProjects = useMemo(
    () =>
      priorityLaneProjectIds
        .map((projectId) => projectsById[projectId])
        .filter((project): project is PortfolioMockProject => Boolean(project)),
    [priorityLaneProjectIds, projectsById]
  )

  const handlePickRandomPriorityProject = () => {
    const randomProjectId = pickRandomProjectIdFromLane(priorityLaneProjectIds)
    if (!randomProjectId) return
    setActiveProjectId(randomProjectId)
  }

  const clearProjectDragPreview = () => {
    if (dragPreviewRef.current) {
      dragPreviewRef.current.remove()
      dragPreviewRef.current = null
    }
  }

  const setProjectDragPreview = (
    event: DragEvent,
    project: PortfolioMockProject
  ) => {
    clearProjectDragPreview()
    const thumbnailUrl =
      project.coverImageUrl ||
      [...project.stages].reverse().find((s) => s.imageUrl?.trim())?.imageUrl

    const preview = document.createElement("div")
    preview.style.position = "fixed"
    preview.style.top = "-9999px"
    preview.style.left = "-9999px"
    preview.style.pointerEvents = "none"
    preview.style.display = "flex"
    preview.style.alignItems = "center"
    preview.style.gap = "8px"
    preview.style.padding = "6px 10px"
    preview.style.borderRadius = "8px"
    preview.style.border = "1px solid #91caff"
    preview.style.background = "#e6f4ff"
    preview.style.boxShadow = "0 8px 18px rgba(0, 0, 0, 0.15)"
    preview.style.maxWidth = "280px"

    if (thumbnailUrl) {
      const wrap = document.createElement("div")
      wrap.style.width = "24px"
      wrap.style.height = "24px"
      wrap.style.borderRadius = "4px"
      wrap.style.overflow = "hidden"
      wrap.style.display = "flex"
      wrap.style.alignItems = "center"
      wrap.style.justifyContent = "center"
      wrap.style.background = "#fafafa"
      wrap.style.border = "1px solid #f0f0f0"
      const image = document.createElement("img")
      image.src = thumbnailUrl
      image.alt = project.title
      image.style.width = "100%"
      image.style.height = "100%"
      image.style.objectFit = "cover"
      image.style.objectPosition = "center"
      wrap.appendChild(image)
      preview.appendChild(wrap)
    }

    const title = document.createElement("span")
    title.textContent = project.title
    title.style.fontSize = "12px"
    title.style.fontWeight = "600"
    title.style.color = "#1f1f1f"
    title.style.whiteSpace = "nowrap"
    title.style.overflow = "hidden"
    title.style.textOverflow = "ellipsis"
    preview.appendChild(title)

    document.body.appendChild(preview)
    dragPreviewRef.current = preview

    event.dataTransfer.setData("text/plain", project.id)
    event.dataTransfer.setDragImage(preview, 20, 16)
  }

  const handleAddReference = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!selectedProject) return
    const file = event.target.files?.[0]
    if (!file) return
    const objectUrl = URL.createObjectURL(file)
    addProjectReference(selectedProject.id, objectUrl)
    event.target.value = ""
  }

  const handleRemoveStageArtwork = useCallback(
    (artworkId: string) => {
      if (!student?.artwork?.[artworkId]) return
      void updateStudent(student.id, {
        [`artwork.${artworkId}.inPortfolio`]: false,
      })
    },
    [student, updateStudent]
  )

  const rotationByArtworkId = useMemo(() => {
    const map: Record<string, number> = {}
    if (!student?.artwork) return map
    for (const [id, art] of Object.entries(student.artwork)) {
      if (art.rotation) map[id] = art.rotation
    }
    return map
  }, [student?.artwork])

  // Mirrors the rotation pattern in StudentArtworkSection / StudentProfileTabs —
  // rotation lives on `student.artwork[id].rotation` and cycles by 90°.
  const handleRotateStageArtwork = useCallback(
    (artworkId: string) => {
      if (!student?.artwork?.[artworkId]) return
      const current = student.artwork[artworkId].rotation ?? 0
      void updateStudent(student.id, {
        [`artwork.${artworkId}.rotation`]: (current + 90) % 360,
      })
    },
    [student, updateStudent]
  )

  const getProjectCoverRotation = useCallback(
    (project: PortfolioMockProject): number => {
      const stage = [...project.stages]
        .reverse()
        .find((s) => s.imageUrl?.trim())
      if (!stage?.artworkId) return 0
      return rotationByArtworkId[stage.artworkId] ?? 0
    },
    [rotationByArtworkId]
  )

  const [categoryDropTarget, setCategoryDropTarget] = useState<
    PortfolioMockProject["category"] | null
  >(null)

  const handleCreateProject = useCallback(
    (input: CreatePortfolioProjectInput) => {
      const result = createProject(input)
      if (!result.ok) {
        message.warning(
          describeArrangementError(result.error, categoryLabelMap)
        )
      }
    },
    [createProject, message]
  )

  // Shared row body for both the main project list and the priority lane.
  // `priorityIndex` (1-based) renders a small badge in the top-right; pass
  // `onRemoveFromPriority` to expose the lane-removal X next to the badge.
  // Wraps `<ProjectRow>` so we can keep the drag/drop closures co-located with
  // the rest of the photo-grid state. Used by both the main list and the
  // priority lane.
  const renderProjectRowBody = (
    project: PortfolioMockProject,
    options: {
      priorityIndex?: number
      onRemoveFromPriority?: () => void
    } = {}
  ) => (
    <ProjectRow
      studentId={studentId}
      project={project}
      planningSummary={planningProjectSummaries[project.id]}
      coverRotation={getProjectCoverRotation(project)}
      onSelectStage={(stageId) => {
        setActiveProjectId(project.id)
        setActiveDetailTab("items")
        setFocusedStageId(stageId)
      }}
      onAddStep={() => {
        setStepModalProjectId(project.id)
        setIsStepModalOpen(true)
      }}
      onDragHandleStart={(event) => {
        event.stopPropagation()
        setDragProjectId(project.id)
        event.dataTransfer.effectAllowed = "move"
        setProjectDragPreview(event, project)
      }}
      onDragHandleEnd={() => {
        setDragProjectId(null)
        setProjectDropZone(null)
        setProjectDropTargetId(null)
        setProjectInsertIndicator(null)
        setCategoryDropTarget(null)
        clearProjectDragPreview()
      }}
      priorityIndex={options.priorityIndex}
      onRemoveFromPriority={options.onRemoveFromPriority}
    />
  )

  const renderProjectGroup = (
    category: PortfolioMockProject["category"],
    label: string,
    groupProjects: PortfolioMockProject[],
    emptyLabel: string,
    countLabel: string,
    isOverLimit: boolean
  ) => {
    // Unassigned is a scanner-managed staging area — can't accept manual category
    // changes, and no "+" button for creating new projects.
    const isUnassigned = category === "unassigned"
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          borderRadius: 8,
          padding: categoryDropTarget === category ? 6 : 0,
          border:
            categoryDropTarget === category
              ? "2px dashed #1677ff"
              : "2px dashed transparent",
          transition: "border-color 0.15s ease, padding 0.15s ease",
        }}
        onDragOver={(event) => {
          if (!dragProjectId) return
          if (isUnassigned) return
          const draggedProject = projectsById[dragProjectId]
          if (!draggedProject || draggedProject.category === category) return
          event.preventDefault()
          event.dataTransfer.dropEffect = "move"
          setCategoryDropTarget(category)
        }}
        onDragLeave={() => {
          setCategoryDropTarget((prev) => (prev === category ? null : prev))
        }}
        onDrop={(event) => {
          if (!dragProjectId) return
          if (isUnassigned) return
          const draggedProject = projectsById[dragProjectId]
          if (!draggedProject || draggedProject.category === category) return
          event.preventDefault()
          const result = updateProject(dragProjectId, { category })
          if (!result.ok) {
            message.warning(
              describeArrangementError(result.error, categoryLabelMap)
            )
          }
          setDragProjectId(null)
          setProjectInsertIndicator(null)
          clearProjectDragPreview()
          setCategoryDropTarget(null)
        }}
      >
        <TimelineSectionHeader
          category={category}
          label={label}
          countLabel={countLabel}
          isOverLimit={isOverLimit}
          categoryTextColorMap={categoryTextColorMap}
          onAddProject={
            isUnassigned
              ? undefined
              : () => {
                  setProjectModalInitialCategory(category)
                  setIsProjectModalOpen(true)
                }
          }
        />
        {groupProjects.length === 0 && (
          <Text type="secondary" style={{ fontSize: 14 }}>
            {emptyLabel}
          </Text>
        )}
        {groupProjects.map((project) => {
          const isActive = selectedProject?.id === project.id
          const isStageMoveTarget =
            !!dragStage && dragStage.projectId !== project.id
          const isDropTarget =
            projectDropZone === "timeline" &&
            projectDropTargetId === project.id &&
            isStageMoveTarget
          const isProjectSwapTarget =
            !!dragProjectId && dragProjectId !== project.id
          const isProjectHoverTarget =
            projectDropZone === "timeline" &&
            projectDropTargetId === project.id &&
            isProjectSwapTarget
          const isDraggedProject = dragProjectId === project.id
          const isInsertTarget =
            isProjectSwapTarget &&
            projectInsertIndicator?.zone === "timeline" &&
            projectInsertIndicator?.projectId === project.id
          const showInsertBefore =
            isInsertTarget && projectInsertIndicator?.position === "before"
          const showInsertAfter =
            isInsertTarget && projectInsertIndicator?.position === "after"

          return (
            <div
              key={project.id}
              data-project-row="true"
              role="button"
              tabIndex={0}
              onClick={() => setActiveProjectId(project.id)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return
                event.preventDefault()
                setActiveProjectId(project.id)
              }}
              onDragOver={(event) => {
                const canHandleProjectSwap =
                  !!dragProjectId && dragProjectId !== project.id
                const canHandleStageMove =
                  !!dragStage && dragStage.projectId !== project.id
                if (!canHandleProjectSwap && !canHandleStageMove) return

                event.preventDefault()
                setProjectDropZone("timeline")
                setProjectDropTargetId(project.id)
                if (canHandleProjectSwap) {
                  const rowRect = event.currentTarget.getBoundingClientRect()
                  const position =
                    event.clientY < rowRect.top + rowRect.height / 2
                      ? "before"
                      : "after"
                  setProjectInsertIndicator({
                    zone: "timeline",
                    projectId: project.id,
                    position,
                  })
                }
                event.dataTransfer.dropEffect = canHandleStageMove
                  ? "copy"
                  : "move"
              }}
              onDragLeave={() => {
                setProjectDropTargetId((prev) =>
                  prev === project.id ? null : prev
                )
                setProjectDropZone((prev) =>
                  prev === "timeline" ? null : prev
                )
                setProjectInsertIndicator((prev) =>
                  prev?.zone === "timeline" && prev?.projectId === project.id
                    ? null
                    : prev
                )
              }}
              onDrop={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setProjectDropZone(null)
                setProjectDropTargetId(null)

                if (dragProjectId && dragProjectId !== project.id) {
                  const placement =
                    projectInsertIndicator?.zone === "timeline" &&
                    projectInsertIndicator?.projectId === project.id
                      ? projectInsertIndicator.position
                      : "before"
                  const result = reorderProjects(
                    dragProjectId,
                    project.id,
                    placement
                  )
                  if (!result.ok) {
                    message.warning(
                      describeArrangementError(result.error, categoryLabelMap)
                    )
                  }
                }

                if (dragStage && dragStage.projectId !== project.id) {
                  moveStageToProject(
                    dragStage.stageId,
                    dragStage.projectId,
                    project.id
                  )
                }

                setDragProjectId(null)
                setProjectInsertIndicator(null)
                clearProjectDragPreview()
                setDragStage(null)
              }}
              style={{
                all: "unset",
                cursor: "pointer",
                display: "block",
                borderRadius: 8,
                border:
                  isActive && !isProjectHoverTarget
                    ? "1px solid #91caff"
                    : "1px solid #f0f0f0",
                padding: "6px 8px",
                minHeight: 146,
                background:
                  isActive && !isProjectHoverTarget ? "#e6f4ff" : "transparent",
                outline: isDropTarget ? "2px dashed #1677ff" : "none",
                opacity: isDraggedProject ? 0.45 : 1,
                transform: isDraggedProject
                  ? "translateY(-6px) scale(1.01)"
                  : "translateY(0)",
                boxShadow: isDraggedProject
                  ? "0 8px 18px rgba(0, 0, 0, 0.15)"
                  : showInsertBefore
                    ? "inset 0 2px 0 #1677ff"
                    : showInsertAfter
                      ? "inset 0 -2px 0 #1677ff"
                      : "none",
                transition: "all 0.15s ease-in-out",
              }}
            >
              {renderProjectRowBody(project, {
                priorityIndex: (() => {
                  const idx = priorityLaneProjectIds.indexOf(project.id)
                  return idx >= 0 ? idx + 1 : undefined
                })(),
              })}
            </div>
          )
        })}
      </div>
    )
  }
  if (!projectState.length) {
    return (
      <>
        <Card size="small">
          <Empty
            description="No AP portfolio projects yet."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsProjectModalOpen(true)}
            >
              New project
            </Button>
          </Empty>
        </Card>
        <PortfolioProjectModal
          open={isProjectModalOpen}
          onClose={() => setIsProjectModalOpen(false)}
          onCreateProject={handleCreateProject}
        />
      </>
    )
  }

  return (
    <Flex col gap={12} style={{ height: "100%", minHeight: 0 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "3fr 1fr",
          gap: 16,
          alignItems: "stretch",
          flex: 1,
          minHeight: 0,
        }}
      >
        <Flex gap={16} style={{ alignItems: "stretch", minHeight: 0 }}>
          <Card
            size="small"
            style={{ width: 420, height: "100%", overflow: "auto" }}
          >
            <Flex col gap={14}>
              <Flex
                col
                gap={6}
                style={{
                  position: "sticky",
                  top: -4,
                  zIndex: 3,
                  background: "#ffffff",
                  paddingTop: 4,
                  paddingBottom: 6,
                }}
              >
                <Flex gap={6} style={{ alignItems: "center" }}>
                  <Text strong style={{ fontSize: 13 }}>
                    Priority Lane ({priorityLaneProjectIds.length}/10)
                  </Text>
                  <Tooltip title="Pick random priority project">
                    <Button
                      icon={<ThunderboltOutlined />}
                      disabled={!priorityLaneProjectIds.length}
                      onClick={handlePickRandomPriorityProject}
                      size="small"
                      type="text"
                    />
                  </Tooltip>
                </Flex>
                <div
                  style={{
                    border: "2px dashed #1677ff",
                    borderRadius: 8,
                    background: "#f0f8ff",
                    padding: 8,
                    minHeight: 52,
                  }}
                  onDragOver={(event) => {
                    if (!dragProjectId) return
                    event.preventDefault()
                    event.dataTransfer.dropEffect = "move"
                  }}
                  onDrop={(event) => {
                    event.preventDefault()
                    if (!dragProjectId) return

                    if (!priorityLaneProjectIds.includes(dragProjectId)) {
                      addProjectToPriorityLane(dragProjectId)
                    }

                    setDragProjectId(null)
                    setProjectDropZone(null)
                    setProjectDropTargetId(null)
                    setProjectInsertIndicator(null)
                    clearProjectDragPreview()
                  }}
                >
                  <Flex col gap={6}>
                    {priorityLaneProjects.map((project) => {
                      const isActive = selectedProject?.id === project.id
                      const isDropTarget =
                        projectDropZone === "priority" &&
                        !!dragProjectId &&
                        dragProjectId !== project.id &&
                        projectDropTargetId === project.id
                      const isInsertTarget =
                        isDropTarget &&
                        projectInsertIndicator?.zone === "priority" &&
                        projectInsertIndicator?.projectId === project.id
                      const showInsertBefore =
                        isInsertTarget &&
                        projectInsertIndicator?.position === "before"
                      const showInsertAfter =
                        isInsertTarget &&
                        projectInsertIndicator?.position === "after"
                      return (
                        <div
                          key={`timeline-priority-${project.id}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveProjectId(project.id)}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ")
                              return
                            event.preventDefault()
                            setActiveProjectId(project.id)
                          }}
                          onDragOver={(event) => {
                            if (
                              !dragProjectId ||
                              dragProjectId === project.id ||
                              !priorityLaneProjectIds.includes(dragProjectId)
                            ) {
                              return
                            }
                            event.preventDefault()
                            setProjectDropZone("priority")
                            setProjectDropTargetId(project.id)
                            const rowRect =
                              event.currentTarget.getBoundingClientRect()
                            const position =
                              event.clientY < rowRect.top + rowRect.height / 2
                                ? "before"
                                : "after"
                            setProjectInsertIndicator({
                              zone: "priority",
                              projectId: project.id,
                              position,
                            })
                          }}
                          onDragLeave={() => {
                            setProjectDropTargetId((prev) =>
                              prev === project.id ? null : prev
                            )
                            setProjectDropZone((prev) =>
                              prev === "priority" ? null : prev
                            )
                            setProjectInsertIndicator((prev) =>
                              prev?.zone === "priority" &&
                              prev?.projectId === project.id
                                ? null
                                : prev
                            )
                          }}
                          onDrop={(event) => {
                            event.preventDefault()
                            if (!dragProjectId || dragProjectId === project.id)
                              return
                            if (!priorityLaneProjectIds.includes(dragProjectId))
                              return

                            const placement =
                              projectInsertIndicator?.zone === "priority" &&
                              projectInsertIndicator?.projectId === project.id
                                ? projectInsertIndicator.position
                                : "before"
                            reorderProjectPriority(
                              dragProjectId,
                              project.id,
                              placement
                            )
                            setDragProjectId(null)
                            setProjectDropZone(null)
                            setProjectDropTargetId(null)
                            setProjectInsertIndicator(null)
                            clearProjectDragPreview()
                          }}
                          style={{
                            all: "unset",
                            cursor: "pointer",
                            display: "block",
                            border: isActive
                              ? "1px solid #91caff"
                              : "1px solid #d6e4ff",
                            borderRadius: 6,
                            padding: 8,
                            background: isActive ? "#e6f4ff" : "#ffffff",
                            boxShadow: showInsertBefore
                              ? "inset 0 2px 0 #1677ff"
                              : showInsertAfter
                                ? "inset 0 -2px 0 #1677ff"
                                : "none",
                            transition: "all 0.15s ease-in-out",
                          }}
                        >
                          {renderProjectRowBody(project, {
                            onRemoveFromPriority: () =>
                              removeProjectFromPriorityLane(project.id),
                          })}
                        </div>
                      )
                    })}
                    {!priorityLaneProjects.length && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Drag projects here to set priority order.
                      </Text>
                    )}
                  </Flex>
                </div>
              </Flex>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Drag projects to reorder or assign priority
              </Text>
              {renderProjectGroup(
                "selected",
                "Selected Work",
                selectedWorkProjects,
                "No selected-work projects yet.",
                selectedCountLabel,
                selectedWorkProjects.length > PORTFOLIO_CATEGORY_LIMITS.selected
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <Flex gap={8} style={{ alignItems: "center" }}>
                  <Text
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: categoryTextColorMap.sustained,
                    }}
                  >
                    Written Evidence
                  </Text>
                </Flex>
                {ESSAY_KEYS_IN_ORDER.map((essayKey) => (
                  <EssayRow
                    key={essayKey}
                    essayKey={essayKey}
                    value={portfolioProfile.statements[essayKey].value ?? ""}
                    isActive={selectedEssayKey === essayKey}
                    onSelect={() =>
                      setActiveProjectId(ESSAY_SELECTION_IDS[essayKey])
                    }
                  />
                ))}
              </div>
              {renderProjectGroup(
                "sustained",
                "Sustained Investigation",
                sustainedProjects,
                "No sustained-investigation projects yet.",
                sustainedCountLabel,
                sustainedProjects.length > PORTFOLIO_CATEGORY_LIMITS.sustained
              )}
              {renderProjectGroup(
                "paused",
                "Paused",
                pausedProjects,
                "No paused projects.",
                `${pausedProjects.length}`,
                false
              )}
              {renderProjectGroup(
                "unassigned",
                "Unassigned",
                unassignedProjects,
                "No unassigned artwork.",
                `${unassignedProjects.length}`,
                false
              )}
            </Flex>
          </Card>

          <Flex
            col
            gap={8}
            style={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              maxWidth: 900,
              overflowX: "hidden",
            }}
          >
            {selectedEssayKey ? (
              <EssayPanel
                key={selectedEssayKey}
                essayKey={selectedEssayKey}
                value={
                  portfolioProfile.statements[selectedEssayKey].value ?? ""
                }
                onCommit={(value) =>
                  updateStatementValue(selectedEssayKey, value)
                }
              />
            ) : selectedProject ? (
              <Card
                size="small"
                style={{
                  flex: 1,
                  height: "100%",
                  overflowX: "hidden",
                  overflowY: "auto",
                  minWidth: 0,
                }}
                title={
                  <Flex col gap={8} style={{ minWidth: 0, maxWidth: "100%" }}>
                    <Flex
                      gap={8}
                      style={{
                        alignItems: "center",
                        minWidth: 0,
                        flexWrap: "wrap",
                      }}
                    >
                      <Text
                        strong
                        ellipsis
                        style={{
                          fontSize: 18,
                          minWidth: 80,
                        }}
                      >
                        {selectedProject.title}
                      </Text>
                      <Tag color={categoryColorMap[selectedProject.category]}>
                        {categoryLabelMap[selectedProject.category]}
                      </Tag>
                      <div style={{ flex: 1 }} />
                      {selectedProject.category !== "unassigned" && (
                        <Popconfirm
                          title="Are you sure?"
                          okText="Yes, delete"
                          cancelText="No"
                          onConfirm={() => deleteProject(selectedProject.id)}
                        >
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            aria-label="Delete project"
                          />
                        </Popconfirm>
                      )}
                    </Flex>
                    <Popover
                      trigger="click"
                      placement="bottomLeft"
                      title="Project details"
                      content={
                        <ProjectDetailsPopoverContent
                          key={selectedProject.id}
                          project={selectedProject}
                          updateProject={updateProject}
                          portfolioProfile={portfolioProfile}
                          updatePortfolioProfile={updatePortfolioProfile}
                        />
                      }
                      styles={{ body: { maxWidth: 480 } }}
                    >
                      <div style={{ cursor: "pointer" }}>
                        <SegmentDetailRow
                          values={[
                            selectedProject.dimensions,
                            selectedProject.materials,
                            selectedProject.processes,
                            selectedProject.ideas,
                          ]}
                        />
                      </div>
                    </Popover>
                  </Flex>
                }
              >
                <PortfolioProjectTabs
                  studentId={studentId}
                  selectedProject={selectedProject}
                  activeDetailTab={activeDetailTab}
                  setActiveDetailTab={setActiveDetailTab}
                  statusOptions={statusOptions}
                  statusColorMap={statusColorMap}
                  dragStage={dragStage}
                  setDragStage={setDragStage}
                  stageDropTargetId={stageDropTargetId}
                  setStageDropTargetId={setStageDropTargetId}
                  setProjectDropTargetId={setProjectDropTargetId}
                  reorderStagesInProject={reorderStagesInProject}
                  deleteStage={deleteStage}
                  updateStage={updateStage}
                  getDueDateStyle={getDueDateStyle}
                  referenceInputRef={referenceInputRef}
                  handleAddReference={handleAddReference}
                  updateProjectReference={updateProjectReference}
                  removeProjectReference={removeProjectReference}
                  focusedStageId={focusedStageId}
                  onFocusedStageHandled={() => setFocusedStageId(null)}
                  onAddStep={() => {
                    if (
                      selectedProject.stages.length >=
                      PORTFOLIO_MAX_STEPS_PER_PROJECT
                    ) {
                      return
                    }
                    setStepModalProjectId(selectedProject.id)
                    setIsStepModalOpen(true)
                  }}
                  maxStepsReached={
                    selectedProject.stages.length >=
                    PORTFOLIO_MAX_STEPS_PER_PROJECT
                  }
                  maxStepsPerProject={PORTFOLIO_MAX_STEPS_PER_PROJECT}
                  onRemoveStageArtwork={handleRemoveStageArtwork}
                  rotationByArtworkId={rotationByArtworkId}
                  onRotateStageArtwork={handleRotateStageArtwork}
                />
              </Card>
            ) : (
              <Card size="small" style={{ flex: 1 }}>
                <Empty
                  description="Select a project to view details."
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              </Card>
            )}
          </Flex>
        </Flex>

        <Card
          ref={commentScrollRef}
          size="small"
          style={{ minHeight: 0, overflow: "auto" }}
          title={
            <Flex gap={8} style={{ alignItems: "center" }}>
              <VerticalAlignTopOutlined />
              <Text strong>
                {selectedEssayKey ? "Essay History" : "Project History"}
              </Text>
            </Flex>
          }
        >
          <Flex col gap={8} style={{ minHeight: 0 }}>
            {selectedProjectTimeline.length === 0 ? (
              <Empty
                description="No comments or item updates yet."
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              selectedProjectTimeline.map((entry) => (
                <PortfolioComment
                  key={entry.id}
                  item={entry}
                  projectState={projectState}
                  statusColorMap={statusColorMap}
                  currentUserId={currentUserId}
                  onClick={(item) => {
                    if (item.eventScope !== "item" || !item.eventEntityId)
                      return
                    setActiveDetailTab("items")
                    setFocusedStageId(item.eventEntityId)
                  }}
                />
              ))
            )}
            {activeSelectionId && (
              <Flex gap={8} style={{ alignItems: "flex-end", marginTop: 8 }}>
                <Input.TextArea
                  value={commentDraft}
                  placeholder="Type a comment... (Enter to send, Shift+Enter for new line)"
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  onChange={(event) => setCommentDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault()
                      if (!commentDraft.trim()) return
                      addComment({
                        message: commentDraft,
                        projectId: activeSelectionId,
                      })
                      setCommentDraft("")
                    }
                  }}
                  style={{ flex: 1 }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => {
                    addComment({
                      message: commentDraft,
                      projectId: activeSelectionId,
                    })
                    setCommentDraft("")
                  }}
                  disabled={!commentDraft.trim()}
                />
              </Flex>
            )}
          </Flex>
        </Card>
      </div>
      {stepModalProjectId && (
        <PortfolioStepModal
          open={isStepModalOpen}
          projectId={stepModalProjectId}
          onClose={() => {
            setIsStepModalOpen(false)
            setStepModalProjectId(undefined)
          }}
          onCreateStep={createStep}
        />
      )}
      <PortfolioProjectModal
        open={isProjectModalOpen}
        onClose={() => {
          setIsProjectModalOpen(false)
          setProjectModalInitialCategory(undefined)
        }}
        onCreateProject={handleCreateProject}
        initialCategory={projectModalInitialCategory}
      />
    </Flex>
  )
}

export default PortfolioPhotoGrid
